import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import "./DICOMEdit.css";

const DICOMEdit = () => {
  const { id } = useParams();
  const token = localStorage.getItem("token");

  const [formData, setFormData] = useState({
    patient: "",
    file_path: "",
    study: "",
    series_id: "",
    side: "LCA",
    primary_angle: "",
    secondary_angle: "",
    dev_notes: "",
    original_upload_path: "",
    all_images_dir: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Fetch existing DICOM data
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/patients/dicoms/${id}/`, {
        headers: { Authorization: `Token ${token}` },
      })
      .then((res) => {
        // Convert `labels` from object to string for the text area
        const dicom = res.data;
        setFormData({
          ...dicom,
          labels: dicom.labels ? JSON.stringify(dicom.labels, null, 2) : "",
        });
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError("Error fetching DICOM for edit");
        setLoading(false);
      });
  }, [id, token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prepare payload
    const payload = { ...formData };

    // Convert label text -> object or null
    if (!payload.labels.trim()) {
      payload.labels = null;
    } else {
      try {
        payload.labels = JSON.parse(payload.labels);
      } catch (err) {
        setError('Labels must be valid JSON (e.g. {"dominant":"Right"}).');
        return;
      }
    }

    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/patients/dicoms/${id}/edit/`,
        payload,
        { headers: { Authorization: `Token ${token}` } }
      );
      setSuccess(true);
      setError('');
    } catch (err) {
      console.error(err);
      setError('Error updating XA DICOM');
      setSuccess(false);
    }
  };

  if (loading) return <p>Loading XA DICOM...</p>;
  if (error && !success) return <p className="error-message">{error}</p>;

  return (
    <div className="dicom-edit-container">
      <h3>Edit XA DICOM</h3>
      {error && <p className="error-message">{error}</p>}
      {success && (
        <p className="success-message">XA DICOM updated successfully!</p>
      )}

      <form onSubmit={handleSubmit} className="dicom-edit-form">
        {/* Patient (Required) - disabled so user can't change it */}
        <div className="form-group">
          <label>
            Patient ID / UUID <span className="required-asterisk">*</span>
          </label>
          <input
            type="text"
            name="patient"
            value={formData.patient || ""}
            onChange={handleChange}
            disabled
            required
          />
        </div>

        {/* File Path (Required) */}
        <div className="form-group">
          <label>
            File Path <span className="required-asterisk">*</span>
          </label>
          <input
            type="text"
            name="file_path"
            value={formData.file_path || ""}
            onChange={handleChange}
            required
          />
        </div>

        {/* Study ID (Optional) */}
        <div className="form-group">
          <label>Study ID (Optional)</label>
          <input
            type="text"
            name="study"
            value={formData.study || ""}
            onChange={handleChange}
          />
        </div>

        {/* Series ID (Optional) */}
        <div className="form-group">
          <label>Series ID (Optional)</label>
          <input
            type="text"
            name="series_id"
            value={formData.series_id || ""}
            onChange={handleChange}
          />
        </div>

        {/* Side (Required) */}
        <div className="form-group">
          <label>
            Side <span className="required-asterisk">*</span>
          </label>
          <select
            name="side"
            value={formData.side || "LCA"}
            onChange={handleChange}
            required
          >
            <option value="LCA">LCA</option>
            <option value="RCA">RCA</option>
            <option value="Both">Both</option>
            <option value="Unknown">Unknown</option>
          </select>
        </div>

        {/* Primary Angle (Optional) */}
        <div className="form-group">
          <label>Primary Angle (Optional)</label>
          <input
            type="number"
            step="0.01"
            name="primary_angle"
            value={formData.primary_angle || ""}
            onChange={handleChange}
          />
        </div>

        {/* Secondary Angle (Optional) */}
        <div className="form-group">
          <label>Secondary Angle (Optional)</label>
          <input
            type="number"
            step="0.01"
            name="secondary_angle"
            value={formData.secondary_angle || ""}
            onChange={handleChange}
          />
        </div>

        {/* Original Upload Path (Required) */}
        <div className="form-group">
          <label>
            Original Upload Path <span className="required-asterisk">*</span>
          </label>
          <input
            type="text"
            name="original_upload_path"
            value={formData.original_upload_path || ""}
            onChange={handleChange}
            required
          />
        </div>

        {/* Dev Notes (Optional) */}
        <div className="form-group">
          <label>Dev Notes (Optional)</label>
          <textarea
            name="dev_notes"
            value={formData.dev_notes || ""}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Labels (Optional)</label>
          <textarea
            name="labels"
            value={formData.labels}
            onChange={handleChange}
            placeholder='{"dominant":"Right","detail":"some note"}'
          />
        </div>

        <div className="form-group">
          <label>All Images Dir (Required)<span className="required-asterisk">*</span></label>
          <input
            type="text"
            name="all_images_dir"
            value={formData.all_images_dir || ""}
            onChange={handleChange}
            required
          />
        </div>

        <button type="submit" className="dicom-edit-btn">
          Update DICOM
        </button>
      </form>
    </div>
  );
};

export default DICOMEdit;
